import { ReactNode, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

// Toolkit imports
import { SandboxLessonDetails } from "sj-ui-toolkit/src/client";
import { getUserLanguagePreference } from "sj-ui-toolkit/src/utils";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { setUserLanguagePreference } from "sj-ui-toolkit/src/utils/languagePreferences";

// Context imports
import { useLearnContext } from "../../../../context/LearnContext";
import { useClaimInternal } from "./Claim/InternalClaimHook";
import { SandboxContext } from "./SandboxContext";
import { useFilesystemInternal } from "./Filesystem";
import { useSubmissionsInternal } from "./Submissions";
import { useAiHintsInternal, HighlightedHintId } from "./AiHints";
import { useTestingInternal } from "./Testing";
import { useProxyInternal } from "./Proxy";
import { useOutputInternal } from "./Output";
import { useLessonInteractionInternal } from "./Interaction/InternalInteractionHook";

interface SandboxProviderProps {
  children: ReactNode;
  lessonDetails: SandboxLessonDetails;
  onLessonFinish: () => void;
  onSandboxReady?: () => void;
  tournamentUuid?: string;
}

const SANDBOX_TIMEOUT_CONFIG = { timeoutMinutes: 15 };

export const SandboxProvider = ({
  children,
  lessonDetails,
  onLessonFinish,
  onSandboxReady,
  tournamentUuid,
}: SandboxProviderProps) => {
  const { isGuest } = useLearnContext();
  const { lessonKey, engines, tests, since } = lessonDetails;
  const { selectedTenant } = useUserContext();
  const [searchParams] = useSearchParams();
  const devTag = searchParams.get("dev") ?? undefined;

  // Create some top-level booleans to use in logic below
  const hasTests = (tests?.testCases?.length ?? 0) > 0;
  const hasEditor = lessonDetails.sandbox?.tabs?.some(tab => tab.type === "editor") ?? false;

  // Overall sandbox state
  const [currentLanguage, setCurrentLanguage] = useState<string | null>(null);
  const [isSandboxLessonComplete, setIsSandboxLessonComplete] = useState(false);

  // Sandbox claiming state & methods
  const {
    session,
    requestSandbox,
    resetSandbox: resetSandboxInternal,
    abandonSandbox,
  } = useClaimInternal(lessonKey, lessonDetails.engines, SANDBOX_TIMEOUT_CONFIG, devTag);

  // Helper method to find a specific app inside of sandboxStatus.apps
  const findAppInState = useCallback(
    (appName: string) => {
      if (!session.sandbox || !session.sandbox.apps) return null;
      return session.sandbox.apps[appName] || null;
    },
    [session.sandbox]
  );

  // When sandbox is ready, call onSandboxReady if provided
  useEffect(() => {
    if (session.overall === "ready" && onSandboxReady) {
      onSandboxReady();
    }
  }, [session.overall, onSandboxReady]);

  // Lesson interaction methods
  const {
    maxStepCompleted,
    allStepsCompleted,
    correctFlagSubmitted,
    reportStepsCompleted,
    reportAllStepsCompleted,
    useHint,
    submitCtfProof,
  } = useLessonInteractionInternal(
    selectedTenant ?? "",
    lessonKey,
    lessonDetails.sandbox.instructions,
    session.readyTimestamp,
    tournamentUuid
  );

  // handles the case where a fs's volumes list doesn't contain the user's current language
  // should only get called in the case of impersonation
  const handleLanguageFallback = (language: string) => {
    setCurrentLanguage(language);
  };

  // Filesystem & editor state - for code editing
  const hasFs = findAppInState("fs-api") !== null;
  const {
    filesystem,
    editor,
    fileLookup,
    beginEditingFile,
    handleEditorChange,
    saveCurrentFile,
    resetFilesystem,
  } = useFilesystemInternal(hasFs, currentLanguage, handleLanguageFallback, session.overall);

  // Submission state
  const {
    submissionsLoaded,
    submissions,
    newSubmission,
    provideAiHints,
    areHintsProvided,
    addPollResponse,
  } = useSubmissionsInternal(
    selectedTenant ?? "",
    lessonKey,
    tests?.testCases || [],
    tournamentUuid,
    since
  );

  // AI Hints state
  const {
    aiHintsEnabled,
    hints,
    isLoading: hintsLoading,
    isLoaded: hintsLoaded,
    error: hintsError,
    generateAiHint,
    provideHintFeedback,
  } = useAiHintsInternal(selectedTenant ?? "", lessonKey, isGuest);
  const [highlightedHint, setHighlightedHint] = useState<HighlightedHintId | null>(null);

  // Internal testing state
  const {
    isTestRunning,
    requestError: testRequestError,
    testStatus,
    runTests: runTestsInternal,
  } = useTestingInternal({
    readyTimestamp: session.readyTimestamp,
    tests: tests || undefined,
    tenantName: selectedTenant ?? undefined,
    tournamentUuid: tournamentUuid,
    replParam: session.replParam,
    handlePollResponse: pollResponse => {
      addPollResponse(pollResponse);
    },
    devTag,
  });

  // Lesson completion logic
  useEffect(() => {
    // only report completion once
    if (isSandboxLessonComplete) return;

    // 3 ways to complete a sandbox lesson
    switch (lessonDetails.completionType) {
      case "tests":
        if (testStatus?.status === "passed") {
          setIsSandboxLessonComplete(true);
        }
        break;
      case "flagSubmission":
        if (correctFlagSubmitted) {
          setIsSandboxLessonComplete(true);
        }
        break;
      case "steps":
        // Don't show the action bar when all steps are complete - user just clicked
        // a button, so just do the finish step
        if (allStepsCompleted) {
          setIsSandboxLessonComplete(true);
          onLessonFinish && onLessonFinish();
        }
        break;
    }
  }, [testStatus, isSandboxLessonComplete, correctFlagSubmitted, allStepsCompleted]);

  // Proxy state
  const proxyUrl = findAppInState("proxy")?.url || null;
  const {
    isIntercepting,
    requests,
    pendingRequestText,
    setInterceptState,
    resumeInterceptedRequest,
  } = useProxyInternal(session.overall, proxyUrl, e => {
    // For now, just log proxy errors to the console
    console.error("Proxy error:", e);
  });

  // Output
  const outputUrl = findAppInState("repl")?.url || null;
  const { outputLines, newLinesCount, resetNewLinesCount, clearOutput } = useOutputInternal(
    outputUrl,
    session.overall === "ready"
  );

  // Acquire a sandbox -> calls requestSandbox from useClaimInternal
  const acquireSandbox = useCallback(
    (engine?: string) => {
      setCurrentLanguage(engine || null);
      requestSandbox(engine);
    },
    [requestSandbox]
  );

  // Reset the current sandbox. Called either without engine (keep current) or with new engine (change languages for repl)
  const resetSandbox = useCallback(
    (engine?: string) => {
      if (engine) {
        setCurrentLanguage(engine);
        setUserLanguagePreference(engine);
      }
      resetFilesystem();
      resetSandboxInternal(engine);
    },
    [resetFilesystem, resetSandboxInternal]
  );

  // Get the current code from the sandbox and send it for testing
  const runTests = useCallback(async (): Promise<object | null> => {
    return runTestsInternal();
  }, [runTestsInternal]);

  // Acquire a sandbox on mount
  useEffect(() => {
    const initAcquire = async () => {
      // get user's language preference to do initial sandbox acquire
      // fallback to first available engine if preference isn't supported
      const userLanguage = await getUserLanguagePreference(engines.length > 0 ? engines : null);
      acquireSandbox(userLanguage ?? engines?.[0]);
    };

    if (session.overall === "initial") {
      initAcquire();
    }
  }, [session.overall, engines]);

  // Merge AI hints with submissions when hints & submissions are loaded
  useEffect(() => {
    if (!areHintsProvided && hintsLoaded && submissionsLoaded) {
      provideAiHints(hints);
    }
  }, [hints, submissions, provideAiHints]);

  // Prepare the context value
  const ContextValue = {
    // Basic sandbox values
    lessonKey,
    lessonDetails,
    currentLanguage,
    isSandboxLessonComplete,

    // Sandbox claim state
    session: session,

    // Methods for acquiring/resetting sandbox
    acquireSandbox,
    resetSandbox,
    abandonSandbox,

    // Filesystem & editor state & methods
    hasEditor,
    beginEditingFile,
    handleEditorChange,
    saveCurrentFile,
    filesystem,
    fileLookup,
    editor,

    // Testing state & methods
    hasTests,
    runTests,
    isTestRunning,
    testStatus,
    testRequestError,

    // Submission state & methods
    submissions,
    newSubmission,

    // AI Hints state
    aiHintsEnabled,
    hints,
    hintsLoading,
    hintsLoaded,
    hintsError,
    highlightedHint,
    setHighlightedHint,
    generateAiHint,
    provideHintFeedback,

    // Proxy state & methods
    isIntercepting,
    requests,
    pendingRequestText,
    setInterceptState,
    resumeInterceptedRequest,

    // Sandbox output
    outputLines,
    newLinesCount,
    resetNewLinesCount,
    clearOutput,

    // Lesson interaction
    maxStepCompleted,
    allStepsCompleted,
    correctFlagSubmitted,
    reportStepsCompleted,
    reportAllStepsCompleted,
    useHint,
    submitCtfProof,
    onLessonFinish,
  };

  return <SandboxContext.Provider value={ContextValue}>{children}</SandboxContext.Provider>;
};
